import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Icons,
  iconSize,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@munifocus/ui';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  COST_CATEGORIES,
  type CostCategory,
  type CreateCostItemDto,
  type InvoiceReading,
} from '@munifocus/contracts-gov';
import { readInvoice, useCreateCostItem } from '../../lib/cost-recovery-api';
import { money, parseHours, parseMoney, todayIso } from '../../lib/cost-recovery-display';

const ACCEPT = 'application/pdf,image/jpeg,image/png,image/heic,image/tiff';

/**
 * Record one cost: what it was, who charged it, when, and how much — with the
 * invoice or receipt behind it.
 *
 * Attaching a PDF invoice reads its totals and offers them; nothing is filled
 * in until the person says so, and the amount saved is always the one on the
 * form. Officer time is hours times a rate, both typed, so the line on the
 * statement shows its own arithmetic.
 */
export function CostItemDialog({
  fileId,
  open,
  onClose,
}: {
  fileId: string;
  open: boolean;
  onClose: () => void;
}) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const create = useCreateCostItem(fileId);
  const fileInput = useRef<HTMLInputElement>(null);

  const [category, setCategory] = useState<CostCategory>('invoice');
  const [description, setDescription] = useState('');
  const [vendor, setVendor] = useState('');
  const [reference, setReference] = useState('');
  const [incurredOn, setIncurredOn] = useState(todayIso());
  const [amount, setAmount] = useState('');
  const [hours, setHours] = useState('');
  const [hourlyRate, setHourlyRate] = useState('');
  const [receipt, setReceipt] = useState<File | null>(null);
  const [reading, setReading] = useState<InvoiceReading | null>(null);
  const [readingBusy, setReadingBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setCategory('invoice');
    setDescription('');
    setVendor('');
    setReference('');
    setIncurredOn(todayIso());
    setAmount('');
    setHours('');
    setHourlyRate('');
    setReceipt(null);
    setReading(null);
  }, [open]);

  const timed = category === 'officer_time';
  const amountCents = parseMoney(amount, lang);
  const hundredths = parseHours(hours);
  const rateCents = parseMoney(hourlyRate, lang);
  const quantityHundredths = timed ? hundredths : 100;
  const unitAmountCents = timed ? rateCents : amountCents;
  const valid =
    description.trim().length > 0 &&
    /^\d{4}-\d{2}-\d{2}$/.test(incurredOn) &&
    incurredOn <= todayIso() &&
    quantityHundredths !== null &&
    unitAmountCents !== null;

  const onPick = async (file: File | null) => {
    setReceipt(file);
    setReading(null);
    if (!file || file.type !== 'application/pdf') return;
    setReadingBusy(true);
    try {
      setReading(await readInvoice(fileId, file));
    } catch {
      setReading(null);
    } finally {
      setReadingBusy(false);
    }
  };

  const applyReading = () => {
    if (!reading) return;
    if (reading.totalCents !== null) {
      setAmount(
        (reading.totalCents / 100).toFixed(2).replace('.', lang.startsWith('fr') ? ',' : '.'),
      );
    }
    if (reading.invoiceNumber && !reference) setReference(reading.invoiceNumber);
    if (reading.invoiceDate && reading.invoiceDate <= todayIso())
      setIncurredOn(reading.invoiceDate);
  };

  const onSave = () => {
    if (!valid || quantityHundredths === null || unitAmountCents === null) return;
    const dto: CreateCostItemDto = {
      category,
      description: description.trim(),
      vendorName: vendor.trim() || undefined,
      reference: reference.trim() || undefined,
      incurredOn,
      quantityHundredths,
      unitAmountCents,
    };
    create.mutate({ dto, receipt }, { onSuccess: onClose });
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{t('gov.costRecovery.item.addTitle')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <TextField
            select
            label={t('gov.costRecovery.item.category')}
            value={category}
            onChange={(e) => setCategory(e.target.value as CostCategory)}
            helperText={category === 'quote' ? t('gov.costRecovery.item.quoteHint') : undefined}
          >
            {COST_CATEGORIES.map((c) => (
              <MenuItem key={c} value={c}>
                {t(`gov.costRecovery.category.${c}`)}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label={t('gov.costRecovery.item.description')}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            required
            multiline
            minRows={2}
            slotProps={{ htmlInput: { maxLength: 500 } }}
          />
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              fullWidth
              label={t('gov.costRecovery.item.vendor')}
              value={vendor}
              onChange={(e) => setVendor(e.target.value)}
              slotProps={{ htmlInput: { maxLength: 200 } }}
            />
            <TextField
              fullWidth
              label={t('gov.costRecovery.item.reference')}
              value={reference}
              onChange={(e) => setReference(e.target.value)}
              slotProps={{ htmlInput: { maxLength: 100 } }}
            />
          </Stack>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              type="date"
              label={t('gov.costRecovery.item.incurredOn')}
              value={incurredOn}
              onChange={(e) => setIncurredOn(e.target.value)}
              slotProps={{ inputLabel: { shrink: true }, htmlInput: { max: todayIso() } }}
            />
            {timed ? (
              <>
                <TextField
                  label={t('gov.costRecovery.item.hours')}
                  value={hours}
                  onChange={(e) => setHours(e.target.value)}
                  error={hours !== '' && hundredths === null}
                  slotProps={{ htmlInput: { inputMode: 'decimal' } }}
                />
                <TextField
                  label={t('gov.costRecovery.item.hourlyRate')}
                  value={hourlyRate}
                  onChange={(e) => setHourlyRate(e.target.value)}
                  error={hourlyRate !== '' && rateCents === null}
                  slotProps={{ htmlInput: { inputMode: 'decimal' } }}
                />
              </>
            ) : (
              <TextField
                label={t('gov.costRecovery.item.amount')}
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                error={amount !== '' && amountCents === null}
                helperText={
                  amount !== '' && amountCents === null
                    ? t('gov.costRecovery.item.amountInvalid')
                    : undefined
                }
                slotProps={{ htmlInput: { inputMode: 'decimal' } }}
              />
            )}
          </Stack>
          {timed && hundredths !== null && rateCents !== null && (
            <Typography variant="body2" color="text.secondary">
              {t('gov.costRecovery.item.timedTotal', {
                total: money(Math.round((hundredths * rateCents) / 100), lang),
              })}
            </Typography>
          )}

          <Box>
            <input
              ref={fileInput}
              type="file"
              accept={ACCEPT}
              hidden
              onChange={(e) => void onPick(e.target.files?.[0] ?? null)}
            />
            <Button
              variant="outlined"
              size="small"
              startIcon={<Icons.Paperclip size={iconSize.sm} />}
              onClick={() => fileInput.current?.click()}
            >
              {receipt ? receipt.name : t('gov.costRecovery.item.attach')}
            </Button>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
              {t('gov.costRecovery.item.attachHint')}
            </Typography>
          </Box>

          {readingBusy && <Alert severity="info">{t('gov.costRecovery.reading.busy')}</Alert>}
          {reading && reading.totalCents !== null && (
            <Alert
              severity={reading.confidence === 'high' ? 'success' : 'warning'}
              action={
                <Button color="inherit" size="small" onClick={applyReading}>
                  {t('gov.costRecovery.reading.use')}
                </Button>
              }
            >
              {t('gov.costRecovery.reading.found', { total: money(reading.totalCents, lang) })}
              {reading.confidence !== 'high' && ` ${t('gov.costRecovery.reading.check')}`}
            </Alert>
          )}
          {reading && reading.totalCents === null && (
            <Alert severity="info">{t('gov.costRecovery.reading.none')}</Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common.actions.cancel')}</Button>
        <Button variant="contained" disabled={!valid || create.isPending} onClick={onSave}>
          {t('gov.costRecovery.item.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
